<template>
    <div class="setup-ai-template-data flex flex-col min-h-0">
        <div class="shrink-0">
            <div class="text-base font-semibold mb-1">
                {{ $t('widget.aiBuilder.dataFields.title') }}
            </div>
        </div>

        <RedesignBaseTabs
            :value="activeMode"
            class="data-fields-tabs flex flex-col flex-1 min-h-0"
            @input="onModeChange"
        >
            <el-tab-pane
                :name="AI_PARAMETER_MODE.MULTIPLE"
                :label="String($t('widget.aiBuilder.dataFields.tab.recommended'))"
            >
                <p class="text-main-xs text-gray-500 mb-4">
                    {{ description }}
                </p>

                <div
                    v-if="isMatching"
                    class="flex items-center justify-center py-8"
                >
                    <span class="text-gray-500">
                        {{ $t('widget.aiBuilder.dataFields.matching') }}
                    </span>
                </div>

                <template v-else>
                    <div
                        v-if="arePromptsStale"
                        class="mb-4"
                    >
                        <div class="setup-ai-template-data__notice flex items-start gap-2 rounded-lg p-2.5 mb-2">
                            <RedesignBaseIcon
                                name="AlertTriangle"
                                class="text-warning shrink-0"
                            />

                            <p class="flex-1 min-w-0 text-main-xs">
                                {{ $t('widget.aiBuilder.dataFields.promptsChanged') }}
                            </p>
                        </div>

                        <RedesignBaseButton
                            size="mini"
                            type="outline"
                            icon="IconSparkles"
                            @click="onGetSuggestions"
                        >
                            {{ $t('widget.aiBuilder.dataFields.getSuggestions') }}
                        </RedesignBaseButton>
                    </div>

                    <div class="flex items-center gap-2 mb-2">
                        <span class="text-main-xs font-semibold uppercase text-gray-500">
                            {{ fieldsHeader }}
                        </span>
                        <RedesignTag
                            v-if="isAiMatched"
                            :tag-content="[ { text: String($t('widget.aiBuilder.dataFields.setByAi')) } ]"
                            padding-classes="px-1.5 py-0.5"
                        />
                    </div>

                    <div class="space-y-2">
                        <AiDataFieldRow
                            v-for="field in fields"
                            :key="field.key"
                            :field="field"
                            @remove="removeField"
                        />
                    </div>

                    <p
                        v-if="!fields.length"
                        class="text-main-xs text-gray-500 py-3"
                    >
                        {{ $t('widget.aiBuilder.dataFields.empty') }}
                    </p>

                    <AiParameterSelect
                        class="mt-3"
                        :value="picked"
                        :label="String($t('widget.aiBuilder.dataFields.add'))"
                        :placeholder="String($t('widget.aiBuilder.dataFields.add'))"
                        :parameter-groups="addableGroups"
                        multiple
                        @input="onAddSelected"
                    />

                    <p
                        v-if="!isLoading && !hasAddableOptions"
                        class="text-main-xs text-gray-500 mt-2"
                    >
                        {{ error || $t('widget.aiBuilder.dataFields.noneAvailable') }}
                    </p>
                </template>
            </el-tab-pane>

            <el-tab-pane
                :name="AI_PARAMETER_MODE.TEMPLATE"
                :label="String($t('widget.aiBuilder.dataFields.tab.advanced'))"
            >
                <p class="text-main-xs text-gray-500 mb-4">
                    {{ $t('widget.aiBuilder.dataFields.advancedDescription') }}
                </p>

                <MarkdownEditor
                    ref="markdownEditorRef"
                    :value="templateValue"
                    mode="edit"
                    editor-type="wysiwyg"
                    height="350px"
                    :enable-parameters="true"
                    @update:value="onTemplateChange"
                >
                    <template #toolbar>
                        <MarkdownParameterToolbar
                            :parameter-groups="parameterGroups"
                            :templates="aiTemplates"
                            :is-loading-templates="isLoadingTemplates"
                            :label="String($t('widget.insertParameter'))"
                            @insert="onInsertParameter"
                            @select-template="onTemplateSelected"
                        />
                    </template>
                </MarkdownEditor>
            </el-tab-pane>
        </RedesignBaseTabs>

        <div class="shrink-0 pt-3">
            <RedesignCheckbox
                v-model="addCallLink"
                @input="emitConfig"
            >
                {{ $t('widget.ai.displayCallLinks') }}
            </RedesignCheckbox>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { TabPane as ElTabPane } from 'element-ui'

import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import MarkdownEditor from '@/components/RedesignBaseComponents/MarkdownEditor.vue'
import MarkdownParameterToolbar from '@/components/RedesignBaseComponents/MarkdownParameterToolbar.vue'
import RedesignBaseTabs from '@/components/RedesignComponents/Tabs/RedesignBaseTabs.vue'
import AiDataFieldRow from '@/components/RedesignComponents/Widgets/Parameters/AiDataFieldRow.vue'
import AiParameterSelect from '@/components/RedesignComponents/Widgets/AiParameterSelect.vue'
import { useAiParameterData } from '@/composables/useAiParameterData'
import {
    useWidgetParameterWizardInject
} from '@/composables/useWidgetParameterWizardProvideInject'
import { aiSuggestedFieldsApi } from '@/api/aiSuggestedFieldsApi'
import { widgetParameterApi, type AiTemplate } from '@/api/widgetParameterApi'
import {
    findPromptTemplateByPrompts,
    useAiPromptTemplates
} from '@/composables/useAiPromptTemplates'
import { SYSTEM_PROMPT_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import { WidgetConfig } from '@/types/dashboard'
import {
    AI_PARAMETER_MODE,
    AiParameterValueJson,
    AiParameterWizardMode,
    IAiDataField,
    ParameterGroup,
    ParameterOption
} from '@/types/widgetParameters'
import i18n from '@/i18n'


const SUGGESTED_FOR_SCRATCH_KEY = 'aiDataFieldsSuggestedFor'

const props = defineProps<{
    value: WidgetConfig
}>()

const emit = defineEmits<{
    (e: 'update:value', value: WidgetConfig): void
}>()

const {
    parameterGroups,
    isLoading,
    error,
    fetchAllData
} = useAiParameterData()
const { fetchPromptTemplates } = useAiPromptTemplates()

const wizard = useWidgetParameterWizardInject()

const selectedKeys = ref<Array<string>>([])

const recentlyRemoved = ref<Array<string>>([])

const picked = ref<Array<string>>([])
const addCallLink = ref(true)
const isMatching = ref(false)
const isAiMatched = ref(false)


const activeMode = ref<AiParameterWizardMode>(AI_PARAMETER_MODE.MULTIPLE)
const templateValue = ref('')
const markdownEditorRef = ref<InstanceType<typeof MarkdownEditor> | null>(null)
const aiTemplates = ref<Array<AiTemplate>>([])
const isLoadingTemplates = ref(false)

const promptsConfig = computed(() => wizard?.getParameter(SYSTEM_PROMPT_PARAMETER_NAME))
const systemPrompt = computed(() => promptsConfig.value?.WidgetParameterValueJson?.system ?? '')
const userPrompt = computed(() => promptsConfig.value?.WidgetParameterValueJson?.user ?? '')

const analysisPreset = computed(() => {
    return findPromptTemplateByPrompts(systemPrompt.value, userPrompt.value)
})

function promptsKeyOf (config?: WidgetConfig) {
    const valueJson = config?.WidgetParameterValueJson
    return `${valueJson?.system ?? ''}::${valueJson?.user ?? ''}`
}

const promptsKey = computed(() => promptsKeyOf(promptsConfig.value))

const suggestedFor = computed(() => {
    return wizard?.scratch.value[SUGGESTED_FOR_SCRATCH_KEY]
})


const listPromptsKey = computed(() => {
    return suggestedFor.value
        ?? promptsKeyOf(wizard?.getOriginalParameter(SYSTEM_PROMPT_PARAMETER_NAME))
})

const arePromptsStale = computed(() => listPromptsKey.value !== promptsKey.value)

const description = computed(() => {
    if (!analysisPreset.value) {
        return i18n.t('widget.aiBuilder.dataFields.description')
    }

    return i18n.t('widget.aiBuilder.dataFields.descriptionFor', {
        analysis: analysisPreset.value.Name
    })
})


const catalogByKey = computed<Record<string, { title: string; group: string }>>(() => {
    const map: Record<string, { title: string; group: string }> = {}

    parameterGroups.value.forEach(group => {
        group.options.forEach(option => {
            if (!map[option.value]) {
                map[option.value] = {
                    title: option.label,
                    group: group.label
                }
            }
        })
    })

    return map
})

function buildField (questionKey: string): IAiDataField {
    const meta = catalogByKey.value[questionKey]

    return {
        key: questionKey,
        title: meta?.title || questionKey,
        group: meta?.group ?? ''
    }
}

const fields = computed(() => selectedKeys.value.map(buildField))

const fieldsHeader = computed(() => {
    const key = isAiMatched.value
        ? 'widget.aiBuilder.dataFields.matched'
        : 'widget.aiBuilder.dataFields.selected'

    return i18n.t(key, { count: fields.value.length })
})

function toOption (questionKey: string) {
    return {
        label: buildField(questionKey).title,
        value: questionKey
    }
}

const addableGroups = computed<Array<ParameterGroup>>(() => {
    const groups: Array<ParameterGroup> = []
    const removed = recentlyRemoved.value.filter(key => !selectedKeys.value.includes(key))

    const offered = new Set<string>([ ...selectedKeys.value, ...removed ])

    if (removed.length) {
        groups.push({
            label: String(i18n.t('widget.aiBuilder.dataFields.recentlyRemoved')),
            options: removed.map(toOption)
        })
    }

    parameterGroups.value.forEach(group => {
        const options: Array<ParameterOption> = []

        group.options.forEach(option => {
            if (offered.has(option.value)) {
                return
            }
            offered.add(option.value)
            options.push(toOption(option.value))
        })

        if (options.length) {
            groups.push({
                label: group.label,
                options
            })
        }
    })

    return groups
})

const hasAddableOptions = computed(() => {
    return addableGroups.value.some(group => group.options.length > 0)
})

function buildValueJson (): AiParameterValueJson {
    return {
        type: activeMode.value,
        data: activeMode.value === AI_PARAMETER_MODE.TEMPLATE
            ? templateValue.value
            : [ ...selectedKeys.value ],
        addCallLink: addCallLink.value
    }
}

function emitConfig () {
    const next = {
        ...props.value,
        WidgetParameterValueJson: buildValueJson()
    } as WidgetConfig

    emit('update:value', next)
}

function addFields (questionKeys: Array<string>) {
    const added = questionKeys.filter(key => !selectedKeys.value.includes(key))
    if (!added.length) {
        return
    }

    selectedKeys.value = [ ...selectedKeys.value, ...added ]
    recentlyRemoved.value = recentlyRemoved.value.filter(key => !added.includes(key))
    emitConfig()
}

function onAddSelected (value: string | Array<string>) {
    picked.value = []
    addFields(Array.isArray(value) ? value : [ value ])
}

function removeField (questionKey: string) {
    selectedKeys.value = selectedKeys.value.filter(key => key !== questionKey)

    if (!recentlyRemoved.value.includes(questionKey)) {
        recentlyRemoved.value = [ questionKey, ...recentlyRemoved.value ]
    }

    emitConfig()
}

function rememberSuggestedFor () {
    if (!wizard) {
        return
    }

    wizard.scratch.value = {
        ...wizard.scratch.value,
        [SUGGESTED_FOR_SCRATCH_KEY]: promptsKey.value
    }
}

function hydrateFromConfig () {
    const valueJson = props.value?.WidgetParameterValueJson as AiParameterValueJson | undefined
    if (!valueJson) {
        return
    }

    addCallLink.value = valueJson.addCallLink !== false

    if (valueJson.type === AI_PARAMETER_MODE.TEMPLATE) {
        activeMode.value = AI_PARAMETER_MODE.TEMPLATE
        templateValue.value = typeof valueJson.data === 'string' ? valueJson.data : ''
    } else if (Array.isArray(valueJson.data)) {
        selectedKeys.value = [ ...valueJson.data ]
    } else if (valueJson.type === AI_PARAMETER_MODE.SINGLE && valueJson.data) {
        selectedKeys.value = [ valueJson.data ]
    }
}

async function fetchAiTemplates () {
    isLoadingTemplates.value = true
    try {
        aiTemplates.value = await widgetParameterApi.getAiTemplateList()
    } finally {
        isLoadingTemplates.value = false
    }
}

function onModeChange (mode: string) {
    activeMode.value = mode as AiParameterWizardMode

    if (activeMode.value === AI_PARAMETER_MODE.TEMPLATE && !aiTemplates.value.length) {
        fetchAiTemplates()
    }

    emitConfig()
}

function onTemplateChange (markdown: string) {
    templateValue.value = markdown
    emitConfig()
}

function onInsertParameter (text: string) {
    markdownEditorRef.value?.insertText(text)
}

function onTemplateSelected (template: AiTemplate) {
    const content = template.Template.replace(/\\n/g, '\n')
    templateValue.value = content
    markdownEditorRef.value?.setMarkdown(content)
    emitConfig()
}

async function matchFields (options: { keepDisplaced?: boolean } = {}) {
    const previousKeys = [ ...selectedKeys.value ]
    isMatching.value = true

    try {
        const suggested = await aiSuggestedFieldsApi.getSuggestedFields({
            systemPrompt: systemPrompt.value,
            userPrompt: userPrompt.value
        })

        if (!suggested.length) {
            return
        }

        selectedKeys.value = Array.from(new Set(suggested))

        recentlyRemoved.value = options.keepDisplaced
            ? previousKeys.filter(key => !selectedKeys.value.includes(key))
            : []
        isAiMatched.value = true
        rememberSuggestedFor()
        emitConfig()
    } finally {
        isMatching.value = false
    }
}

function onGetSuggestions () {
    matchFields({ keepDisplaced: true })
}

onMounted(() => {
    hydrateFromConfig()
    fetchAllData()
    fetchPromptTemplates()

    if (activeMode.value === AI_PARAMETER_MODE.TEMPLATE) {
        fetchAiTemplates()
        return
    }

    if (suggestedFor.value === promptsKey.value) {
        isAiMatched.value = true
        return
    }

    if (!suggestedFor.value && !selectedKeys.value.length) {
        matchFields()
    }
})
</script>

<style lang="scss">

.setup-ai-template-data {
    &__notice {
        background-color: color-mix(in srgb, var(--warning-actions) 8%, transparent);
        border: 1px solid color-mix(in srgb, var(--warning-actions) 25%, transparent);
    }

    .data-fields-tabs {
        .el-tabs__header {
            @apply shrink-0 p-1 mb-4 rounded-lg border-0;
            background-color: color-mix(in srgb, var(--primary-actions) 10%, transparent);
        }

        .el-tabs__nav-wrap::after,
        .el-tabs__active-bar {
            @apply hidden;
        }

        .el-tabs__nav.is-top {
            @apply flex w-full p-0;
            float: none;
        }

        .el-tabs__item {
            @apply flex-1 flex items-center justify-center rounded-md px-3 text-sm font-medium;
            height: 32px;
            line-height: normal;
            border: 1px solid transparent;
            color: var(--placeholders) !important;

            &:hover {
                color: var(--mainTextColor-color) !important;
            }

            &.is-active {
                @apply font-semibold;
                background-color: var(--light-bg);
                border-color: var(--ui-lines);
                color: var(--mainTextColor-color) !important;
                box-shadow:
                    0 2px 6px 0 rgba(0, 0, 0, 0.1),
                    0 2px 4px -2px rgba(0, 0, 0, 0.1);
            }
        }

        .el-tabs__content {
            @apply flex-1 min-h-0 overflow-y-auto pr-1;
        }
    }
}
</style>
